// «Хүргэлт үүсгэх» modal (Ord_SalesModals 2.4.4): the selected orders, an optional source warehouse, one primary action.
import { Box, Button, Group, Select, Text } from "@mantine/core";
import { TruckIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IAuth } from "../../store/Auth";
import { message } from "../../utils/message";
import { backendMessageKey } from "../order/backend-messages";
import { OrderActionCard } from "../order/order-action-card";
import { useSalesActions } from "../order/order-actions";
import { ReasonTooltip } from "../ui";
import { useCreateDelivery, useWarehouseOptions } from "./hooks";
import classes from "./delivery-create-modal.module.css";

interface Props {
  orderIds: string[];
  /** Order numbers shown as chips (same order as `orderIds`). */
  orderCodes?: string[];
  onCancel: () => void;
  onSuccess: () => void;
}

export function DeliveryCreateModal({ orderIds, orderCodes = [], onCancel, onSuccess }: Props) {
  const { t } = useTranslation(["supplierSales", "common"]);
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);
  const businessId = user?.currentBusinessId ?? null;

  const { warehouses, isLoading: loadingWarehouses } = useWarehouseOptions(businessId);
  const { submit, submitting } = useCreateDelivery();
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const { permissionState } = useSalesActions();
  const perm = permissionState("deliveryCreate");

  const handleConfirm = async () => {
    try {
      const result = await submit(orderIds, warehouseId);
      if (result.failures.length > 0) {
        message.error(
          t("supplierSales:sales.createDeliveryPartial", {
            assigned: result.warehouseAssigned,
            total: result.createdCount,
          }),
        );
      } else {
        message.success(t("common:messages.requestSuccess"));
      }
      onSuccess();
    } catch (err) {
      const raw = (err as Error).message ?? "";
      message.error(backendMessageKey(raw) || t("common:messages.requestFailed"));
    }
  };

  const orderCount = orderIds.length;

  return (
    <OrderActionCard
      title={t("supplierSales:sales.createDelivery")}
      onClose={onCancel}
      loading={submitting}
      footer={
        <>
          <Button variant="default" onClick={onCancel} type="button" disabled={submitting}>
            {t("common:actions.dismiss")}
          </Button>
          <ReasonTooltip reason={perm.reason}>
            <Button leftSection={<TruckIcon size={16} />} onClick={handleConfirm} disabled={orderCount === 0 || submitting}>
              {t("supplierSales:sales.createDeliveryButton", { count: orderCount })}
            </Button>
          </ReasonTooltip>
        </>
      }
    >
      <Box>
        <Text fz={14} fw={500} mb="xs">
          {t("supplierSales:sales.createDeliverySelected", { count: orderCount })}
        </Text>
        <Group gap="xs">
          {(orderCodes.length > 0 ? orderCodes : orderIds).map((code) => (
            <Text key={code} span className={classes.chip}>
              {code}
            </Text>
          ))}
        </Group>
      </Box>
      <Select
        label={t("supplierSales:sales.sourceWarehouseLabel")}
        placeholder={t("supplierSales:sales.sourceWarehousePlaceholder")}
        description={t("supplierSales:sales.sourceWarehouseHint")}
        data={warehouses.map((w) => ({ value: w.id, label: w.name }))}
        value={warehouseId}
        onChange={setWarehouseId}
        searchable
        clearable
        disabled={loadingWarehouses}
        nothingFoundMessage={t("common:messages.empty")}
      />
    </OrderActionCard>
  );
}
